import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";
import { GET_ANNOTATIONS_LIST_BY_IDS_QUERY } from "../../queries/annotationQueries";

export function useAnnotations(annotationsIds) {
  const [organizedOriginalAnnotations, setOrganizedOriginalAnnotations] =
    useState({});
  const [filteredOriginalAnnotations, setFilteredOriginalAnnotations] =
    useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Use useLazyQuery for manual control
  const [getAnnotations] = useLazyQuery(GET_ANNOTATIONS_LIST_BY_IDS_QUERY, {
    fetchPolicy: "network-only",
  });

  const organizeAnnotations = useCallback((ids, annotationsArray) => {
    return Object.keys(ids).reduce((acc, instanceId) => {
      if (!ids[instanceId]) {
        return acc;
      }
      acc[instanceId] = ids[instanceId]
        .map(id => annotationsArray.find(annotation => annotation?._id === id))
        .filter(annotation => annotation !== undefined);
      return acc;
    }, {});
  }, []);

  const removeTypename = useCallback(obj => {
    if (Array.isArray(obj)) {
      return obj.map(removeTypename);
    } else if (obj !== null && typeof obj === "object") {
      const newObj = {};
      for (const key in obj) {
        if (key !== "__typename") {
          newObj[key] = removeTypename(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  }, []);

  const getFilteredAnnotations = useCallback(
    annotationsData => {
      const result = {};
      for (const instanceId in annotationsData) {
        if (annotationsData.hasOwnProperty(instanceId)) {
          if (!result[instanceId]) {
            result[instanceId] = [];
          }
          annotationsData[instanceId].forEach(annotation => {
            if (annotation) {
              const { __typename, ...cleanAnnotation } = annotation;
              result[instanceId].push(removeTypename(cleanAnnotation));
            }
          });
        }
      }
      return result;
    },
    [removeTypename]
  );

  // ✅ Extract fetchAnnotations as a separate callback for reuse
  const fetchAnnotations = useCallback(async () => {
    const allAnnotationIds = Object.values(annotationsIds).flat();

    if (allAnnotationIds.length === 0) {
      setOrganizedOriginalAnnotations({});
      setFilteredOriginalAnnotations({});
      return { organizedAnnotations: {}, filteredAnnotations: {} };
    }

    try {
      setLoading(true);
      setError(null);

      const { data } = await getAnnotations({
        variables: { ids: allAnnotationIds },
        fetchPolicy: "network-only", // ✅ Force fresh data
      });

      if (data?.annotationsByIds) {
        const organized = organizeAnnotations(
          annotationsIds,
          data.annotationsByIds
        );
        const filtered = getFilteredAnnotations(organized);

        setOrganizedOriginalAnnotations(organized);
        setFilteredOriginalAnnotations(filtered);

        // Return the fresh data for immediate use
        return {
          organizedAnnotations: organized,
          filteredAnnotations: filtered,
        };
      }

      return { organizedAnnotations: {}, filteredAnnotations: {} };
    } catch (err) {
      setError(err);
      return { organizedAnnotations: {}, filteredAnnotations: {} };
    } finally {
      setLoading(false);
    }
  }, [
    annotationsIds,
    getAnnotations,
    organizeAnnotations,
    getFilteredAnnotations,
  ]);

  // ✅ Add refresh function similar to useDigitalTwinData
  const refreshAnnotations = useCallback(async () => {
    return await fetchAnnotations();
  }, [fetchAnnotations]);

  useEffect(() => {
    fetchAnnotations();
  }, [fetchAnnotations]);

  return {
    filteredOriginalAnnotations,
    organizedOriginalAnnotations,
    loading,
    error,
    refreshAnnotations, // ✅ Export refresh function
  };
}
